import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sidebar as PrimeSidebar } from "primereact/sidebar";
import { Menu } from "primereact/menu";
import { Button } from "../ui/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faUser,
  faRightFromBracket,
  faBell,
  faEllipsisVertical,
  faTimes,
  faPalette,
} from "@fortawesome/free-solid-svg-icons";
import {
  LayoutDashboard,
  FileText,
  Users as UsersIcon,
  List as ListIcon,
  CreditCard as CreditCardIcon,
  BarChart3,
  Settings as SettingsIcon,
  Building2,
  Layers,
  UserCog,
  Wrench,
  MailCheck,
  MailOpen,
  LayoutTemplate,
  ChevronDown,
  ChevronRight,
  ReceiptText,
  type LucideIcon,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { PermissionVariables } from "../../shared/constant/PermissionVariables";
import { getUserByEmail } from "../../service/user/userService";
import { getTenantById } from "../../service/tenant/TenantService";
import {
  createNotification,
  deleteNotifications,
  existingNotificationCount,
  getNotificationsStatusCount,
  getUnreadNotifications,
  getUserNotifications,
  markAllNotificationsAsRead,
  markAsRead,
} from "../../service/notifications/NotificationService";
import { Badge } from "primereact/badge";
import { OverlayPanel } from "primereact/overlaypanel";
import { changeMode, changeTheme } from "../../store/slices/themeSlice";
import { applyTheme } from "../../utils/applyTheme";
import { Toast } from "primereact/toast";
import { showToast, hideToast } from "../../store/slices/toastSlice";
import { Loader } from "../ui/Loader";
import { setLocalization } from "../../store/slices/localizationSlice";
import {
  findSettingsByUserIdAndKey,
  findUpdateAndCreateSettings,
  getAllAppSettings,
  getAllLanguages,
  setSessionLanguage,
} from "../../service/common/commonService";
import { SessionLanguage } from "../../dtos/common/SettingsLanguage";
import { Dropdown } from "primereact/dropdown";
import { US, IN, FR, DE, ES, CN } from "country-flag-icons/react/3x2";
import { getTenantDetailByTenantId } from "../../service/tenantDetails/TenantDetailsService";
import { setProfilePic } from "../../store/slices/profilePicSlice";
import { logoutApi } from "../../service/logout/logoutService";
import { clearAuthStorage, getFromStorage, getValidIntegerOrNull } from "./../../utils/storageUtils";
import { clearError } from "../../store/slices/errorSlice";
import { setEdition } from "../../store/slices/editionSlice";
import { getEditionByTenantId } from "../../service/tenantEdition/TenantEditionService";

interface MainLayoutProps {
  children: React.ReactNode;
}

interface NotificationsDto {
  id: number;
  tenantId: number;
  userId: number;
  message: string;
  status: string;
  createdAt: Date;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const userId = localStorage.getItem("userId");
  const tenantId = localStorage.getItem("tenantId");
  const validTenantId =
    tenantId === "null" || tenantId === null ? null : JSON.parse(tenantId);
  const [visible, setVisible] = React.useState(false);
  const [selectedNotifications, setSelectedNotifications] = useState<number[]>(
    []
  );
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const userPermissions = useSelector(
    (state: any) => state.permission.permissions
  );
  const user = useSelector((state: any) => state.user.userDetails.user);
  const [userName, setUserName] = useState<string>("");
  const [tenantName, setTenantName] = useState<string>("");
  const [notifications, setNotifications] = useState<any>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const isLogin = useSelector((state: any) => state.login.islogin);
  const [visibleRight, setVisibleRight] = useState(false);
  const [allNotifications, setAllNotifications] = useState<
    NotificationsDto[] | []
  >([]);
  const [activeNotificationsCount, setActiveNotificationsCount] =
    useState<number>(0);
  const [unReadNotifications, setUnReadNotifications] = useState<
    NotificationsDto[] | []
  >([]);
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const op = useRef<OverlayPanel>(null);
  const err = useSelector((state: any) => state?.error);
  const appMode = useSelector((state: any) => state.theme.appMode);
  const themeName = useSelector((state: any) => state.theme.themeName);
  const toast = useRef<Toast>(null);
  const toastState = useSelector((state: any) => state.toast);
  const [showDialog, setShowDialog] = useState(false);
  const [language, setLanguage] = React.useState(null);
  const [allLanguage, setAllLanguage] = React.useState<any[]>([]);
  const isLoading = useSelector((state: any) => state.loading.isloading);
  const userLogo = useSelector((state: any) => state.profPic.profilePic);
  const localization = useSelector(
    (state: any) => state.localization?.localization
  );

  // You can extend this mapping based on your needs
  const nameToCountryMap: Record<string, React.ElementType> = {
    en: US,
    sp: ES,
    fr: FR,
    gr: DE,
    ch: CN,
    hn: IN,
  };

  const languageOptions = allLanguage?.map((lan) => {
    // Make sure lan.Name matches the keys in nameToCountryMap (case insensitive)
    const languageKey = lan.Name.toLowerCase();
    const FlagIcon = nameToCountryMap[languageKey] || US;

    return {
      label: lan.DisplayName, // Simple label for the dropdown
      value: lan,
      // This will be used in itemTemplate
      template: (
        <div
          className="flag-container"
          style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
        >
          <span>{lan.DisplayName}</span>
          <FlagIcon width={30} />
        </div>
      ),
    };
  });

  const getLanguages = async () => {
    const storedLanguage = localStorage.getItem("Language");
    const allLanguages = await getAllLanguages(dispatch);
    allLanguages?.forEach((lan: any) => {
      const languageKey = lan.Name.toLowerCase();
      if (languageKey === storedLanguage?.toLocaleLowerCase()) {
        setLanguage(lan);
      }
    });
    setAllLanguage(allLanguages);
  };

  const getByEmail = async () => {
    if (!user) return;
    const response = await getUserByEmail(dispatch, { email: user });

    if (!response) return;
    setUserName(response?.userName);
  };
  const isValidEditionId = (): number | null => {
    const editionIdRaw = localStorage.getItem("editionId");
    const editionId = Number(editionIdRaw);

    return Number.isInteger(editionId) && editionId > 0 ? editionId : null;
  };

  const getNotifications = async () => {
    const editionId = isValidEditionId();

    if (!editionId) return;
    const notifications = await getUserNotifications(dispatch);
    setAllNotifications(notifications);
  };

  const getTenant = async () => {
    const tenantId = localStorage.getItem("tenantId");
    if (!tenantId) return;

    const response = await getTenantById(tenantId, dispatch);
    if (!response) return;
    // ❌ No active subscription → redirect
    if (!response.editionId) {
      localStorage.removeItem("editionId"); // important
      dispatch(setEdition({ editionId: null }));
      navigate("/subscription-plan", { replace: true });
      return;
    }

    // ✅ Active subscription
    localStorage.setItem("editionId", response.editionId.toString());
    dispatch(setEdition({ editionId: response.editionId }));
    setTenantName(response.tenancyName);
  };

  const getActiveNotificationsCount = async () => {
    const editionId = isValidEditionId();

    if (!editionId) return;
    const unreadInput = {
      status: "unread",
    };
    const unreadCount = await getNotificationsStatusCount(
      dispatch,
      unreadInput
    );
    setActiveNotificationsCount(unreadCount);
  };

  const getAllUnreadNotifications = async () => {
    const storedUserId = localStorage.getItem("userId");
    if (storedUserId) {
      const input = {
        userId: JSON.parse(storedUserId),
      };
      const notifications = await getUnreadNotifications(dispatch, input);
      await setUnReadNotifications(notifications);
    } else {
      setUnReadNotifications([]);
    }
  };

  const handleLanguage = async (e: any) => {
    if (e?.target) {
      e.preventDefault();
    }

    setLanguage(e.value);
    const credentials: SessionLanguage = {
      name: e.value.Name,
    };
    const userId = localStorage.getItem("userId");
    const tenantId = localStorage.getItem("tenantId");
    if (userId && tenantId) {
      const settingsInput = {
        userId: JSON.parse(userId),
        tenantId: JSON.parse(tenantId),
        name: "Language",
        value: e.value.Name,
      };
      await findUpdateAndCreateSettings(dispatch, settingsInput);
      await setSessionLanguage(dispatch, credentials);
      localStorage.setItem("Language", e.value.Name);
      const localization = await getAllAppSettings(dispatch);
      dispatch(setLocalization(localization?.values));
    }
  };

  const getUserTheme = async () => {
    const storedUserId = localStorage.getItem("userId");
    if (!storedUserId)
      return "UserId is not available, unable to fetch settings.";

    const userId = JSON.parse(storedUserId);

    // Prepare input payloads
    const modeInput = { userId, key: "Mode" };
    const languageInput = { userId, key: "Language" };
    const themeInput = { userId, key: "Theme" };

    // Fetch settings from DB
    const [userSettings, languageSettings, themeSettings] = await Promise.all([
      findSettingsByUserIdAndKey(dispatch, modeInput),
      findSettingsByUserIdAndKey(dispatch, languageInput),
      findSettingsByUserIdAndKey(dispatch, themeInput),
    ]);

    // Handle Theme (fallback to localStorage, then default 'blue')
    const storedThemeName =
      themeSettings?.value ||
      (() => {
        const stored = localStorage.getItem("Theme-Name");
        return stored ? JSON.parse(stored).themeName : "lightBlue";
      })();

    dispatch(changeTheme({ themeName: storedThemeName, appMode: "" }));
    applyTheme(storedThemeName);

    // Handle Mode
    const mode =
      userSettings?.value ||
      (() => {
        const stored = localStorage.getItem("App-Mode");
        return stored ? JSON.parse(stored).appMode : "light";
      })();

    // Set mode using Redux action
    dispatch(
      changeMode({
        appMode: mode,
        themeName: mode, // required to satisfy action payload
      })
    );

    // Handle Language
    const languageValue =
      languageSettings?.value ||
      (() => {
        const storedLang = localStorage.getItem("Language");
        return storedLang ? storedLang : "en";
      })();

    // Mirror the resolved preferences to localStorage so they survive logout
    // (clearAuthStorage keeps them) and the login splash can restore the theme.
    localStorage.setItem(
      "Theme-Name",
      JSON.stringify({ themeName: storedThemeName })
    );
    localStorage.setItem("App-Mode", JSON.stringify({ appMode: mode }));
    localStorage.setItem("Language", languageValue);

    // Update local language state
    await getLanguages();
  };

  useEffect(() => {
    getUserTheme();
  }, []);

  const getDataAccordingToFilter = async () => {
    if (activeFilter === "all") {
      await getNotifications();
    } else {
      await getAllUnreadNotifications();
    }
  };

  const isSettingsActive =
    location.pathname.startsWith("/users") ||
    location.pathname.startsWith("/settings") ||
    location.pathname.startsWith("/tenantPaymentSettings") ||
    location.pathname.startsWith("/tenants") ||
    location.pathname.startsWith("/roles") ||
    location.pathname.startsWith("/editions");

  React.useEffect(() => {
    if (isSettingsActive) {
      setSettingsOpen(true);
    }
  }, [location.pathname]);

  const parseTenantId = (value: string | null) => {
    if (value === null) return undefined;     // key missing → logout
    if (value === "null") return null;        // host user
    if (value === "undefined") return undefined;

    const num = Number(value);
    return Number.isInteger(num) && num > 0 ? num : undefined;
  };

  const parseUserId = (value: string | null) => {
    if (!value || value === "null" || value === "undefined") return undefined;

    const num = Number(value);
    return Number.isInteger(num) && num > 0 ? num : undefined;
  };

  useEffect(() => {
    async function checkSubscriptionAndNotify() {
      try {
        // ✅ Safe parsing
        const rawTenantId = localStorage.getItem("tenantId");
        const rawUserId = localStorage.getItem("userId");
        const tenantId = parseTenantId(rawTenantId);
        const userId = parseUserId(rawUserId);
        // 🔴 INVALID SESSION → LOGIN
        if (tenantId === undefined || userId === undefined) {
          clearAuthStorage();
          navigate("/login", { replace: true });
          return;
        }

        // ✅ HOST USER (tenantId = null) → no subscription check
        if (tenantId === null) {
          return;
        }

        // 1️⃣ Fetch edition info
        const response = await getEditionByTenantId(dispatch, tenantId);

        // ❌ No active subscription → redirect
        if (!response?.editionId) {
          localStorage.removeItem("editionId");
          dispatch(setEdition({ editionId: null }));
          navigate("/subscription-plan", { replace: true });
          return;
        }

        // 2️⃣ Calculate expiry
        if (!response.endDate) return;

        const expiryDate = new Date(response.endDate);
        if (isNaN(expiryDate.getTime())) return;

        const diffInDays = Math.ceil(
          (expiryDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
        );

        // ❌ expired or not in range
        if (diffInDays <= 0 || diffInDays > 3) return;

        // 3️⃣ Avoid duplicate notification (case-insensitive / partial match)
        const message = `⚠️ Your subscription will expire in ${diffInDays} day(s). Please renew.`
        const alreadyExists = await existingNotificationCount(dispatch, {
          message
        });

        if (alreadyExists > 0) {
          await getNotifications();
          await getActiveNotificationsCount();
          return;
        }

        // 4️⃣ Create notification
        await createNotification(dispatch, {
          tenantId,
          userId: [userId],
          message,
          status: "unread"
        });

        await getNotifications();
        await getActiveNotificationsCount();
      } catch (error) {
        console.error("Subscription check failed:", error);
      }
    }
    if (userPermissions?.includes(PermissionVariables?.notifications)) {
      checkSubscriptionAndNotify();
    }
  }, []);


  useEffect(() => {
    const storedToken = localStorage.getItem("token");

    if (!storedToken) {
      console.error("No authentication token found");
      return;
    }

    // ✅ validateToken REQUIRES "Bearer <token>"
    const token = storedToken.startsWith("Bearer ")
      ? storedToken
      : `Bearer ${storedToken}`;

    // ✅ Correct port + no /ws
    const socket = new WebSocket(
      `${process.env.REACT_APP_WEB_SOCKET_URL}?token=${encodeURIComponent(token)}`
    );

    socket.onopen = () => {
      console.log("WebSocket connection established");
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === "notification") {
          setNotifications((prev: any) => [data.data, ...prev]);
          setUnreadCount((prev) => prev + 1);
        }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    socket.onerror = (err) => {
      console.error("WebSocket error", err);
    };

    return () => {
      socket.close();
    };
  }, []);

  const confirm = () => {
    setShowDialog(true);
  };

  // const accept =async () => {
  //   await logoutApi();
  //   localStorage.clear();

  //   sessionStorage.clear();
  //   navigate("/login");
  // };
  const accept = async () => {
    try {
      await logoutApi();   // try calling API
    } catch (err) {
      console.error("Logout API failed, continuing logout:", err);
      dispatch(
        showToast({
          severity: "error",
          summary: "Logout error",
          detail: "Unable to reach server. Logging out locally"
        })
      );
    }

    // Always run these even if API fails (keep UI preferences like theme/mode)
    clearAuthStorage();
    sessionStorage.clear();
    navigate("/login");
  };


  const reject = () => {
    setShowDialog(false);
  };

  useEffect(() => {
    if (notifications.length > 0) {
      dispatch(
        showToast({
          severity: "info",
          summary: localization?.Notification || "Notification",
          detail: localization?.NewNotification || "New notification",
        })
      );
    }
  }, [notifications, dispatch]);

  useEffect(() => {
    if (userPermissions?.includes(PermissionVariables?.notifications)) {
      getNotifications();
      getActiveNotificationsCount();
    }
  }, []);

  // Add effect to handle toast visibility
  useEffect(() => {
    if (toastState.visible) {
      toast.current?.show({
        severity: toastState.severity,
        summary: toastState.summary,
        detail: toastState.detail,
        life: toastState.life,
      });
      dispatch(hideToast());
    }
  }, [
    toastState.visible,
    toastState.severity,
    toastState.summary,
    toastState.detail,
    toastState.life,
    dispatch,
  ]);

  // Helper function to create standard menu items
  const createMenuItem = (
    label: string,
    path: string,
    Icon: LucideIcon,
    requiredPermission?: string
  ) => {
    if (requiredPermission && !userPermissions?.includes(requiredPermission)) {
      return null;
    }

    return {
      label,
      command: () => navigate(path),
      template: (item: any, options: any) => {
        const isActive = location.pathname.startsWith(path);
        return (
          <a
            className={`flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${isActive ? "sidebar-active" : ""} ${isActive
              ? appMode === "dark"
                ? "dark_menuActive"
                : "bg-cream text-charcoal"
              : appMode === "dark"
                ? "text-light-gray hover:bg-medium-gray hover:bg-opacity-50"
                : "sidebar-link-text hover:text-charcoal hover:bg-light-gray"
              }`}
            onClick={options.onClick}
          >
            <Icon size={18} strokeWidth={1.75} className="shrink-0" />
            <span>{item.label}</span>
          </a>
        );
      },
    };
  };

  // Helper function to create admin sub-items
  const createAdminSubItem = (
    label: string,
    path: string,
    Icon: LucideIcon,
    requiredPermission?: string
  ) => {
    if (!requiredPermission || !userPermissions?.includes(requiredPermission)) {
      return null;
    }

    const isActive = location.pathname.startsWith(path);
    return (
      <a
        key={path}
        className={`cursor-pointer flex items-center gap-2.5 px-3 py-2 my-1 text-sm rounded-lg transition-colors ${isActive ? "sidebar-active" : ""} ${isActive
          ? appMode === "dark"
            ? "dark_menuActive"
            : "bg-cream text-charcoal"
          : appMode === "dark"
            ? "text-light-gray hover:bg-medium-gray hover:bg-opacity-50"
            : "sidebar-link-text hover:text-charcoal hover:bg-light-gray"
          }`}
        onClick={() => navigate(path)}
      >
        <Icon size={16} strokeWidth={1.75} className="shrink-0" />
        <span>{label}</span>
      </a>
    );
  };

  // Menu structure data
  const menuItemsData = [
    {
      label: localization?.Dashboard || "Dashboard",
      path: "/dashboard",
      icon: LayoutDashboard,
    },
    {
      label: localization?.Invoices || "Invoices",
      path: "/invoices",
      icon: FileText,
      permission: PermissionVariables.invoices,
    },
    {
      label: localization?.Clients || "Clients",
      path: "/client",
      icon: UsersIcon,
      permission: PermissionVariables.client,
    },
    {
      label: localization?.Items || "Items",
      path: "/item",
      icon: ListIcon,
      permission: PermissionVariables.items,
    },
    {
      label: localization?.PaymentSchedule || "Payment Schedule",
      path: "/paymentSchedule",
      icon: CreditCardIcon,
      permission: PermissionVariables.paymentSchedule,
    },
    {
      label: localization?.Reports || "Reports",
      path: "/report",
      icon: BarChart3,
      permission: PermissionVariables.reports,
    },
    // { label: localization?.Reports || "Ask AI", path: "/askAi", icon: faChartBar, permission: PermissionVariables.reports },
  ];

  // Admin sub-items data
  const adminSubItems =
    validTenantId === null
      ? [
        {
          label: localization?.User || "Users",
          path: "/users",
          icon: UsersIcon,
          permission: PermissionVariables.user,
        },
        {
          label: localization?.Template || "Templates",
          path: "/template",
          icon: LayoutTemplate,
          permission: PermissionVariables.templates,
        },
        {
          label: localization?.Tenant || "Tenants",
          path: "/tenants",
          icon: Building2,
          permission: PermissionVariables.tenant,
        },
        {
          label: localization?.Role || "Roles",
          path: "/roles",
          icon: UserCog,
          permission: PermissionVariables.role,
        },
        {
          label: localization?.Edition || "Editions",
          path: "/editions",
          icon: Layers,
          permission: PermissionVariables.edition,
        },
        {
          label: localization?.Settings || "Settings",
          path: "/settings",
          icon: Wrench,
          permission: PermissionVariables.settings,
        },
        // {
        //   label:
        //     localization?.InvoicePaymentSettings ||
        //     "Invoice Payment Settings",
        //   path: "/tenantPaymentSettings",
        //   icon: faMoneyCheckDollar,
        //   permission: PermissionVariables.createPaymentSettings,
        // },
        {
          label: localization?.MailSettings || "Mail Settings",
          path: "/mailSettings",
          icon: MailCheck,
          permission: PermissionVariables.mailSettings,
        },
        {
          label: localization?.MailTemplate || "Mail Template",
          path: "/mailTemplateSettings",
          icon: MailOpen,
          permission: PermissionVariables.mailTemplate,
        },
      ]
      : [
        {
          label: localization?.User || "Users",
          path: "/users",
          icon: UsersIcon,
          permission: PermissionVariables.user,
        },
        {
          label: localization?.Template || "Templates",
          path: "/template",
          icon: LayoutTemplate,
          permission: PermissionVariables.templates,
        },
        {
          label: localization?.Role || "Roles",
          path: "/roles",
          icon: UserCog,
          permission: PermissionVariables.role,
        },
        {
          label: localization?.Edition || "Editions",
          path: "/editions",
          icon: Layers,
          permission: PermissionVariables.edition,
        },
        {
          label: localization?.Settings || "Settings",
          path: "/settings",
          icon: Wrench,
          permission: PermissionVariables.settings,
        },
        // {
        //   label:
        //     localization?.InvoicePaymentSettings ||
        //     "Invoice Payment Settings",
        //   path: "/tenantPaymentSettings",
        //   icon: faMoneyCheckDollar,
        //   permission: PermissionVariables.createPaymentSettings,
        // },
      ];

  // Generate standard menu items
  const standardMenuItems = menuItemsData
    .map((item) =>
      createMenuItem(item.label, item.path, item.icon, item.permission)
    )
    .filter(Boolean); // Remove null items (filtered by permissions)

  // Create Administration menu item
  const adminMenuItem = userPermissions?.includes(
    PermissionVariables.administration
  )
    ? [
      {
        label: localization?.Administration || "Administration",
        template: (item: any) => (
          <div className="">
            <div
              className={`flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-lg transition-colors cursor-pointer ${isSettingsActive ? "sidebar-active" : ""} ${isSettingsActive
                ? appMode === "dark"
                  ? "bg-charcoal text-gold"
                  : "bg-cream text-charcoal"
                : appMode === "dark"
                  ? "text-light-gray hover:bg-medium-gray hover:bg-opacity-50"
                  : "sidebar-link-text hover:text-charcoal hover:bg-light-gray"
                }`}
              onClick={() => setSettingsOpen(!settingsOpen)}
            >
              <div className="flex items-center gap-2.5">
                <SettingsIcon size={18} strokeWidth={1.75} className="shrink-0" />
                <span>{item.label}</span>
              </div>
              {settingsOpen ? (
                <ChevronDown size={16} strokeWidth={2} className="shrink-0" />
              ) : (
                <ChevronRight size={16} strokeWidth={2} className="shrink-0" />
              )}
            </div>

            {settingsOpen && (
              <div
                className={`p-0 m-0 mx-1.5  ${appMode === "dark"
                  ? "border-l border-medium-gray"
                  : "border-l border-light-gray"
                  }`}
              >
                {adminSubItems.map((subItem) =>
                  createAdminSubItem(
                    subItem.label,
                    subItem.path,
                    subItem.icon,
                    subItem.permission
                  )
                )}
              </div>
            )}
          </div>
        ),
      },
    ]
    : [];

  // Combine all menu items
  const menuItems: any[] = [...standardMenuItems, ...adminMenuItem];

  const notificationHeader = (
    <div
      className={`flex items-center py-3 px-4 ${appMode === "dark"
        ? "border-b border-medium-gray"
        : "border-b border-light-gray"
        }`}
    >
      <h5
        className={`text-lg font-semibold m-0 ${appMode === "dark" ? "text-gold" : "text-gold"
          }`}
      >
        <FontAwesomeIcon
          icon={faBell}
          className={`mr-2 ${appMode === "dark" ? "text-gold" : "text-gold"}`}
        />
        {localization?.Notifications || "Notifications"}
      </h5>
    </div>
  );

  const handleDeleteNotification = async (notificationId: number) => {
    const input = {
      notificationIds: [notificationId],
    };
    await deleteNotifications(dispatch, input);
    await getDataAccordingToFilter();
    await getActiveNotificationsCount();
    setSelectedNotifications([]);
  };

  const handleMarkAsRead = async (id: number) => {
    const input = {
      notificationIds: [id],
    };
    await markAsRead(dispatch, input);
    await getActiveNotificationsCount();
    await getNotifications();
  };

  const handleMarkAllAsRead = async () => {
    await markAllNotificationsAsRead();
    await getActiveNotificationsCount();
  };

  const handleCheckboxChange = (id: number) => {
    setSelectedNotifications((prev) =>
      prev.includes(id) ? prev.filter((n) => n !== id) : [...prev, id]
    );
  };

  const handleMarkSelectedAsRead = async () => {
    const input = {
      notificationIds: selectedNotifications,
    };
    await markAsRead(dispatch, input);
    await getActiveNotificationsCount();
    await getDataAccordingToFilter();
    setSelectedNotifications([]);
  };


  const show = (severity: any, summary: string, detail: string) => {
    toast && toast?.current?.show({ severity, summary, detail });
  };
  const getLogo = async () => {
    const tenantIdString = localStorage.getItem("tenantId");
    if (!tenantIdString) {
      console.log("No tenant ID found, skipping tenant details fetch");
      return;
    }

    const tenantId = JSON.parse(tenantIdString);
    const response = await getTenantDetailByTenantId(dispatch, tenantId);
    dispatch(setProfilePic(response?.logo));
    // setUserLogo(response.logo)
  };
  //Dark theme class toggle
  useEffect(() => {
    if (appMode === "dark") {
      // `darkTheme` drives our custom CSS; `dark` drives Tailwind `dark:` utilities
      document.body.classList.add("darkTheme", "dark");
    } else {
      document.body.classList.remove("darkTheme", "dark");
    }
  }, [appMode]);

  // Keep the global brand colours in sync with the selected theme
  useEffect(() => {
    applyTheme(themeName);
  }, [themeName]);

  useEffect(() => {
    setVisible(err?.isError);
  }, [err]);

  useEffect(() => {
    if (!isLogin) {
      navigate("/login");
    }
  }, [isLogin, navigate]);

  useEffect(() => {
    getByEmail();
    getTenant();
  }, [user]);
  useEffect(() => {
    getLogo();
  }, []);
  useEffect(() => {
    if (err?.errorMessage?.length > 0) {
      show("error", localization?.Error || "Error", err?.errorMessage || "");

    }
    // Clear error AFTER showing toast
    dispatch(clearError());
  }, [err, dispatch]);

  return (
    <div
      className={`h-screen flex flex-col overflow-hidden ${appMode === "dark" ? "darkTheme" : "lightTheme"
        }`}
    >
      <Toast ref={toast} />
      {isLoading && <Loader label={localization?.Loading || "Loading..."} />}
      {showDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="confirmDialog bg-whiterounded-lg shadow-xl max-w-md overflow-hidden rounded-md sm:w-[450px] w-[90%]">
            <div className="p-dialog-header px-6 py-1 flex items-center border-b border-light-gray">
              <svg
                className="w-6 h-6 text-gold mr-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <h3 className="text-lg font-medium surface-900 m-0">
                {localization?.Confirmation || "Confirmation"}
              </h3>
            </div>

            <div className="px-6 py-4 bg-cream">
              <p className="text-medium-gray">
                {localization?.LogoutConfirm ||
                  "Are you sure you want to logout?"}
              </p>
            </div>

            <div className="px-6 py-3 bg-cream flex justify-end space-x-3 border-t border-x-0 border-b-0 border-light-gray border-solid border-gray-300">
              <button
                onClick={reject}
                className="text-sm px-3 py-2 rounded-md h-10 font-medium border-gold text-gold hover:text-white transition-all p-button p-button-secondary no_button"
              >
                {localization?.No || "No"}
              </button>

              <button
                onClick={accept}
                className="px-4 py-2 bg-gold border-gold border-1 text-white hover:opacity-90 transition-colors p-button"
              >
                {localization?.Yes || "Yes"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <header
        className={`${appMode === "dark"
          ? "bg-charcoal shadow-md border-b border-medium-gray"
          : "bg-white shadow-md border-b border-light-gray"
          } py-3 px-4 flex justify-between items-center flex-wrap gap-4`}
      >
        <div className="flex items-center">
          <Button
            variant="text"
            onClick={() => setSidebarVisible(true)}
            className={`lg:hidden mr-2 ${appMode === "dark"
              ? "text-light-gray hover:bg-medium-gray hover:bg-opacity-50"
              : "text-charcoal hover:bg-light-gray"
              } h-8 w-8 flex items-center justify-center rounded-full`}
            style={appMode === "dark" ? undefined : { color: "var(--charcoal)" }}
          >
            <FontAwesomeIcon icon={faBars} />
          </Button>
          <span className={`flex justify-center items-center gap-3 text-xl font-bold ${appMode === "dark" ? "text-white" : "text-charcoal"}`}>
            {userLogo ? (
              <img
                src={userLogo}
                alt={localization?.BusinessLogo || "Business Logo"}
                className="w-10 h-10 rounded-full object-cover cursor-pointer"
                onClick={() => navigate("/dashboard")}
              />
            ) : (
              // Themed fallback icon when no business logo is set — recolours
              // with the active brand theme via --primaryColor.
              <span
                onClick={() => navigate("/dashboard")}
                className="w-10 h-10 rounded-full flex items-center justify-center cursor-pointer shrink-0 border"
                style={{
                  backgroundColor: "var(--boxShadow)",
                  borderColor: "var(--primaryColor)",
                  color: "var(--primaryColor)",
                }}
                aria-label={localization?.BusinessLogo || "Business Logo"}
              >
                <ReceiptText size={20} strokeWidth={1.75} />
              </span>
            )}
            {tenantName == null
              ? localization?.Host || "Host"
              : typeof tenantName === "string" && tenantName?.length == 0
                ? localization?.Default || "Default"
                : tenantName}
          </span>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center">
            <Dropdown
              className="upper-navbar-language-dropdown mr-3 h-8"
              options={languageOptions}
              value={language}
              onChange={handleLanguage}
              placeholder={localization?.English || "English"}
              itemTemplate={(option) => option.template}
              optionLabel="label" // This tells the dropdown what property to show as selected value
            />

            {/* Theme (Appearance page) */}
            <Button
              variant="text"
              onClick={() => navigate("/appearance")}
              className={`mr-3 ${appMode === "dark"
                ? "text-light-gray hover:bg-medium-gray hover:bg-opacity-50"
                : "text-charcoal hover:bg-light-gray"
                } h-8 w-8 flex items-center justify-center rounded-full`}
              style={appMode === "dark" ? undefined : { color: "var(--charcoal)" }}
              title={localization?.Appearance || "Appearance"}
            >
              <FontAwesomeIcon icon={faPalette} />
            </Button>

            {((isValidEditionId() && getValidIntegerOrNull("tenantId")) || getValidIntegerOrNull("tenantId") === null) && <Button
              variant="text"
              onClick={() => setVisibleRight(true)}
              className={`mr-0 relative ${appMode === "dark"
                ? "text-light-gray hover:bg-medium-gray hover:bg-opacity-50"
                : "text-medium-gray hover:bg-light-gray"
                } h-8 w-8 flex items-center justify-center rounded-full`}
              style={appMode === "dark" ? undefined : { color: "var(--medium-gray)" }}
            >
              <FontAwesomeIcon icon={faBell} className="text-lg" />
              {activeNotificationsCount > 0 && (
                <Badge
                  value={activeNotificationsCount}
                  severity="warning"
                  className="absolute -top-1 -right-1"
                />
              )}
            </Button>}
          </div>
          <div className="flex items-center">
            <div
              className={`flex items-center mr-4 py-2 px-3 rounded-md shadow-sm ${appMode === "dark"
                ? "bg-charcoal border border-medium-gray"
                : "bg-cream border border-light-gray"
                }`}
            >
              <FontAwesomeIcon
                icon={faUser}
                className={`${appMode === "dark" ? "text-light-gray" : "text-medium-gray"
                  } mr-2`}
              />
              <span
                className={`text-sm font-medium ${appMode === "dark" ? "text-light-gray" : "text-charcoal"
                  }`}
              >
                {userName.toLocaleUpperCase()}
              </span>
            </div>
            <Button
              variant="text"
              onClick={() => {
                confirm();
              }}
              className={`${appMode === "dark"
                ? "text-light-gray hover:bg-medium-gray hover:bg-opacity-50"
                : "text-medium-gray hover:bg-light-gray"
                } h-8 w-8 flex items-center justify-center rounded-full`}
              style={appMode === "dark" ? undefined : { color: "var(--medium-gray)" }}
            >
              <FontAwesomeIcon icon={faRightFromBracket} />
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar - update with dark mode classes */}
      {location.pathname != "/subscription-plan" && (
        <PrimeSidebar
          visible={sidebarVisible}
          onHide={() => setSidebarVisible(false)}
          className={`p-sidebar-sm ${appMode === "dark" ? "dark-sidebar" : ""}`}
          style={{ width: "240px" }}
        >
          <div
            className={`sidebar-nav p-4 ${appMode === "dark" ? "bg-charcoal text-white" : ""
              }`}
          >
            {/* <h2
              className={`text-base font-semibold mb-4 px-3 mt-0 pt-0 ${appMode === "dark" ? "text-light-gray" : "text-white"
                }`}
            >
              {localization?.InvoiceManagement || "Invoice Management"}
            </h2> */}
            <Menu
              model={menuItems}
              className={`w-full border-none ${appMode === "dark" ? "dark-menu" : ""
                }`}
            />
          </div>
        </PrimeSidebar>
      )}

      {/* Notifications Sidebar - update with dark mode classes */}
      <PrimeSidebar
        visible={visibleRight}
        position="right"
        onHide={() => setVisibleRight(false)}
        className={`p-sidebar-md ${appMode === "dark" ? "dark-sidebar" : ""}`}
        style={{ width: "380px" }}
        header={notificationHeader}
      >
        <div
          className={`p-4 h-full flex flex-col ${appMode === "dark" ? "bg-charcoal text-white" : ""
            }`}
        >
          <div
            className={`sticky top-0 pb-3 mb-4 border-b ${appMode === "dark" ? "border-medium-gray" : "border-light-gray"
              }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setActiveFilter("all");
                    getNotifications();
                  }}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${activeFilter === "all"
                    ? appMode === "dark"
                      ? "bg-charcoal text-gold border border-gold"
                      : "bg-cream text-gold border border-gold"
                    : appMode === "dark"
                      ? "bg-medium-gray bg-opacity-50 text-light-gray border border-medium-gray hover:bg-medium-gray"
                      : "bg-light-gray text-charcoal border border-light-gray hover:bg-medium-gray hover:bg-opacity-20"
                    }`}
                >
                  {localization?.All || "All"}
                </button>
                <button
                  onClick={() => {
                    setActiveFilter("unread");
                    getAllUnreadNotifications();
                  }}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center ${activeFilter === "unread"
                    ? appMode === "dark"
                      ? "bg-charcoal text-gold border border-gold"
                      : "bg-cream text-gold border border-gold"
                    : appMode === "dark"
                      ? "bg-medium-gray bg-opacity-50 text-light-gray border border-medium-gray hover:bg-medium-gray"
                      : "bg-light-gray text-charcoal border border-light-gray hover:bg-medium-gray hover:bg-opacity-20"
                    }`}
                >
                  {localization?.Unread || "Unread"}
                  {activeNotificationsCount > 0 && (
                    <span className="ml-1.5 bg-gold text-white text-xs rounded-full px-1.5 py-0.5">
                      {activeNotificationsCount}
                    </span>
                  )}
                </button>
              </div>
              <button
                type="button"
                aria-label={localization?.NotificationOptions || "Notification options"}
                title={localization?.NotificationOptions || "Notification options"}
                className="notif-menu-btn"
                onClick={(e) => op?.current?.toggle(e)}
              >
                <FontAwesomeIcon icon={faEllipsisVertical} />
              </button>

              <OverlayPanel
                ref={op}
                className={`shadow-lg rounded-lg ${appMode === "dark" ? "dark-overlay" : ""
                  }`}
              >
                <div className="py-1">
                  <button
                    className={`w-full text-left px-4 py-2 text-sm ${appMode === "dark"
                      ? "hover:text-light-gray hover:bg-medium-gray hover:bg-opacity-50"
                      : "text-charcoal hover:bg-light-gray"
                      } transition-colors`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkAllAsRead();
                      getDataAccordingToFilter();
                      op.current?.hide();
                    }}
                  >
                    {localization?.MarkAllAsRead || "Mark all as read"}
                  </button>
                  <button
                    className={`w-full text-left px-4 py-2 text-sm transition-colors ${selectedNotifications?.length === 0
                      ? appMode === "dark"
                        ? "text-medium-gray cursor-not-allowed"
                        : "text-medium-gray cursor-not-allowed"
                      : appMode === "dark"
                        ? "text-light-gray hover:bg-medium-gray hover:bg-opacity-50"
                        : "text-charcoal hover:bg-light-gray"
                      }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkSelectedAsRead();
                      op.current?.hide();
                    }}
                    disabled={selectedNotifications?.length === 0}
                  >
                    {localization?.MarkSelectedAsRead ||
                      "Mark selected as read"}
                  </button>
                </div>
              </OverlayPanel>
            </div>
          </div>

          {/* Notifications List */}
          <div className="flex-grow overflow-auto notif-scroll">
            {(activeFilter === "all" ? allNotifications : unReadNotifications)
              ?.length === 0 ? (
              <div className="text-center py-12">
                <FontAwesomeIcon
                  icon={faBell}
                  size="2x"
                  className={
                    appMode === "dark" ? "text-medium-gray" : "text-medium-gray"
                  }
                />
                <p
                  className={
                    appMode === "dark" ? "text-medium-gray" : "text-medium-gray"
                  }
                >
                  {localization?.NoNotificationsYet || "No notifications yet"}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {(activeFilter === "all"
                  ? allNotifications
                  : unReadNotifications
                )?.map((notification: NotificationsDto) => (
                  <div
                    key={notification.id}
                    className={`rounded-lg p-3 shadow-sm ${notification.status === "unread"
                      ? appMode === "dark"
                        ? "bg-charcoal border border-gold"
                        : "bg-cream border border-gold"
                      : appMode === "dark"
                        ? "bg-charcoal border border-medium-gray"
                        : "bg-white border border-light-gray"
                      }`}
                  >
                    <div className="flex items-start">
                      <div className="flex items-center mr-2 pt-1">
                        <input
                          type="checkbox"
                          checked={selectedNotifications.includes(
                            notification.id
                          )}
                          onChange={() => handleCheckboxChange(notification.id)}
                          className={`h-4 w-4 rounded border-light-gray focus:ring-gold ${appMode === "dark"
                            ? "bg-medium-gray border-medium-gray"
                            : ""
                            }`}
                        />
                      </div>
                      <div className="flex-grow">
                        <p
                          className={`mb-1 text-sm ${notification.status === "unread"
                            ? appMode === "dark"
                              ? "font-semibold text-gold"
                              : "font-semibold text-charcoal"
                            : appMode === "dark"
                              ? "font-normal text-light-gray"
                              : "font-normal text-charcoal"
                            }`}
                        >
                          {notification.message}
                        </p>
                        <div className="flex justify-between items-center">
                          <small
                            className={
                              appMode === "dark"
                                ? "text-medium-gray"
                                : "text-medium-gray"
                            }
                            style={{ fontSize: "0.75rem" }}
                          >
                            {new Date(
                              notification.createdAt
                            ).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </small>
                          {notification.status === "unread" && (
                            <button
                              className={`text-xs font-medium ${appMode === "dark"
                                ? "text-gold hover:text-gold hover:opacity-80"
                                : "text-gold hover:text-gold hover:opacity-80"
                                } transition-colors`}
                              onClick={() => handleMarkAsRead(notification.id)}
                            >
                              {localization?.MarkAsRead || "Mark as read"}
                            </button>
                          )}
                        </div>
                      </div>
                      <button
                        className={`ml-2 ${appMode === "dark"
                          ? "text-medium-gray hover:text-alert-red"
                          : "text-medium-gray hover:text-alert-red"
                          } transition-colors`}
                        onClick={() =>
                          handleDeleteNotification(notification.id)
                        }
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </PrimeSidebar>

      {/* Desktop layout - update with dark mode classes */}
      <div className="flex flex-1 min-h-0">
        {/* Sidebar for desktop */}
        {location.pathname != "/subscription-plan" && (
          <aside
            className={`hidden lg:block w-56 overflow-y-auto no-scrollbar ${appMode === "dark"
              ? "bg-medium-gray shadow-md"
              : "shadow-sm"
              }`}
            style={
              appMode === "dark"
                ? undefined
                : { backgroundColor: "var(--primaryColor)" }
            }
          >
            <div className="sidebar-nav py-4 px-3">
              {/* <h2
                className={`text-base font-semibold mb-4 px-3 ${appMode === "dark" ? "text-light-gray" : "text-white"
                  }`}
              >
                {localization?.InvoiceManagement || "Invoice Management"}
              </h2> */}
              <Menu
                model={menuItems}
                className={`w-full border-none ${appMode === "dark" ? "dark-menu" : ""
                  }`}
              />
            </div>
          </aside>
        )}

        {/* Main content */}
        <main
          className={`flex-1 p-4 overflow-y-auto overflow-x-hidden ${appMode === "dark" ? "asas text-cream" : "bg-cream"
            }`}
        >
          {children}
        </main>
      </div>
    </div>
  );
};
